import { z } from "zod";
/**
 * Address information data schema (internal use)
 */
export const zAddressDataSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
    addressShort: z.string().nullish(),
    addressLocality: z.string().nullish(),
});
/**
 * Address information class with encapsulated methods
 * Provides abstraction layer for working with address data
 */
export class AddressInfo {
    constructor(data) {
        this.addressShort = data.addressShort;
        this.addressLocality = data.addressLocality;
        this.latitude = data.latitude;
        this.longitude = data.longitude;
    }
    /**
     * Get unified display address.
     * Logic: if both fields are present and differ — join as "locality, short".
     * If equal — return one. If only one — return it.
     */
    getAddress() {
        if (!this.addressShort && !this.addressLocality)
            return "";
        if (!this.addressShort)
            return this.addressLocality;
        if (!this.addressLocality)
            return this.addressShort;
        if (this.addressShort === this.addressLocality)
            return this.addressShort;
        return `${this.addressLocality}, ${this.addressShort}`;
    }
    /**
     * Get short/formatted address
     */
    getAddressShort() {
        return this.addressShort || null;
    }
    /**
     * Get locality/city information
     */
    getAddressLocality() {
        return this.addressLocality || null;
    }
    /**
     * Get latitude coordinate
     */
    getLatitude() {
        return this.latitude;
    }
    /**
     * Get longitude coordinate
     */
    getLongitude() {
        return this.longitude;
    }
    /**
     * Get formatted coordinates string
     */
    getCoordinatesString() {
        return `${this.latitude.toFixed(6)}, ${this.longitude.toFixed(6)}`;
    }
    /**
     * Export to plain object for serialization/DTO conversion
     * @internal - should only be used in mappers
     */
    toPlainObject() {
        return {
            addressShort: this.addressShort,
            addressLocality: this.addressLocality,
            latitude: this.latitude,
            longitude: this.longitude,
        };
    }
    /**
     * Create from plain object (for mappers)
     * @internal - should only be used in mappers
     */
    static fromPlainObject(data) {
        return new AddressInfo({
            addressShort: data.addressShort,
            addressLocality: data.addressLocality,
            latitude: data.latitude,
            longitude: data.longitude,
        });
    }
}
